import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useGetEmployees } from "../hooks/useEmployeeApi";
import { EmployeeList } from "../components/employees/EmployeeList";
import Container from "../components/ui/Container";
import Loader from "../components/ui/Loader";

export default function EmployeeListPage() {
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [employeeFilter, setEmployeeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const queryClient = useQueryClient();

  const {
    data: employeeData,
    isLoading,
    isError,
    error,
  } = useGetEmployees({ page, limit });

  // ✅ Filter employees by name, employeeId, email, mobile & status
  const filteredEmployees = useMemo(() => {
    if (!employeeData?.employees) return [];
    const filter = employeeFilter.toLowerCase();
    return employeeData.employees.filter((emp) => {
      const matchesName = emp.name?.toLowerCase().includes(filter);
      const matchesEmployeeId = emp.employeeId?.toLowerCase().includes(filter);
      const matchesEmail = emp.email?.toLowerCase().includes(filter);
      const matchesMobile = emp.mobileNo?.toLowerCase().includes(filter);
      const matchesCity = emp.correspondenceAddress?.toLowerCase().includes(filter);
      const matchesDesignation = emp.designation?.toLowerCase().includes(filter);
      const matchesStatus = emp.status === statusFilter;

      return matchesStatus && (
        matchesName ||
        matchesEmployeeId ||
        matchesEmail ||
        matchesMobile ||
        matchesCity ||
        matchesDesignation
      );
    });
  }, [employeeData?.employees, employeeFilter, statusFilter]);
  if (isError) {
    return (
      <div className="text-center text-red-500 p-8">
        Error: {error.message}
      </div>
    );
  }

  return (
    <Container>
      <div className="flex justify-between items-center ">
        <h1 className="text-xl font-bold mb-2">Employee Directory</h1>
        <Link
          to="/employee/create"
          className="px-4 py-2 bg-[#2C4E86] text-white rounded hover:bg-blue-900"
        >
          + Add Employee
        </Link>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
      <div>
        <p className="font-semibold py-1">Search Employee</p>
      <input
          type="text"
          placeholder="Search by name, ID, email or mobile"
          value={employeeFilter}
          onChange={(e) => setEmployeeFilter(e.target.value)}
          className="border w-64 px-3 py-2 rounded-md"
        />
      </div>

        <div className="flex gap-2">
          <button
            onClick={() => setStatusFilter("active")}
            className={`px-4 py-2 rounded-md border ${
              statusFilter === "active"
                ? "bg-[#2C4E86] text-white"
                : "bg-white text-gray-700"
            }`}
          >
            Active
          </button>
          <button
            onClick={() => setStatusFilter("inactive")}
            className={`px-4 py-2 rounded-md border ${
              statusFilter === "inactive"
                ? "bg-[#2C4E86] text-white"
                : "bg-white text-gray-700"
            }`}
          >
            Inactive
          </button>
        </div>
      </div>

      {isLoading && page === 1 ? (
        <Loader />
      ) : (
        <>
          {filteredEmployees.length > 0 ? (
            <EmployeeList
              employees={filteredEmployees}
              statusFilter={statusFilter}
              onStatusChange={() => queryClient.invalidateQueries(["employees"])}
            />
          ) : (
            <div className="text-center text-gray-500 py-8">
              No {statusFilter} employees found.
            </div>
          )}

          <div className="flex justify-between items-center mt-4">
            <span className="text-sm text-gray-700">
              Page {employeeData?.page || 1} of {employeeData?.pages || 1}
            </span>
            <div className="space-x-2">
              <button
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
                disabled={isLoading || !employeeData || employeeData.page <= 1}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border rounded hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={
                  isLoading ||
                  !employeeData ||
                  employeeData.page >= employeeData.pages
                }
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border rounded hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </Container>
  );
}
